import { AppUtilsModule } from './../../../app-utils.module';
import { environment } from './../../../../environments/environment.prod.universal';
import { AppGlobalsModule } from 'src/app/app-globals.module';

import { Injectable, OnDestroy, DOCUMENT, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { HistoryRouteService } from '../history-route/history-route.service';
import { AnalyticsService } from '../analytics/analytics.service';

@Injectable({
    providedIn: 'root',
})
export class MetaTagService implements OnDestroy {
    private _titleManager = inject(Title);
    private _historyRoute = inject(HistoryRouteService);
    private meta = inject(Meta);
    private _analytics = inject(AnalyticsService);
    private _document = inject<Document>(DOCUMENT);

    public pageTitle: string = '';
    public displayTitle: string = '';
    public historyTitlesStack: string[] = [];

    public newTitle: Subject<string> = new Subject<string>();
    public changedTitle = this.newTitle.asObservable();

    ngOnDestroy(): void {
        if (this.newTitle) {
            this.newTitle.unsubscribe();
        }
    }

    public setMetaTag(name: string, content: string): void {
        this.meta.updateTag({ name: name, content: content });
    }

    public removeMetaTag(name: string): void {
        this.meta.removeTag(`name='${name}'`);
    }

    public setMetaTagByProperty(property: string, content: string, itemprop: string | null = null): void {
        if (itemprop) {
            this.meta.updateTag({ property: property, content: content, itemprop: itemprop });
        } else {
            this.meta.updateTag({ property: property, content: content });
        }
    }

    public setMetaDescription(description: string): void {
        this.setMetaTag('description', description);
        this.setMetaTag('twitter:description', description);
        this.setMetaTagByProperty('og:description', description);
    }

    public setPageTitle(title: string) {
        this.pageTitle = title;
        this.newTitle.next(this.pageTitle);
        let titleToSet = this.pageTitle;
        if (!title.includes('כתבות באתר ישיבה')) {
            titleToSet = this.pageTitle + ' | ' + $localize`:@@yeshivaWebsite:אתר ישיבה`;
        }
        this._titleManager.setTitle(titleToSet);
        this.setMetaTagByProperty('og:title', titleToSet);
        this.setMetaTagByProperty('twitter:title', titleToSet);

        // This is not need anymore in Google Analytics 4, since they added "Enhanced measurement"
        if (!AppGlobalsModule.isServer) {
            // Maybe it's needed, attempt to restore it.
            this._analytics.page_view(this.pageTitle);
            // this._analytics.track('set', 'page', location.pathname);
            // this._analytics.track('set', 'location', window.location.href);
            // this._analytics.virtualPage('pageview');
        }
    }

    public setCanonicalUrl(url: string): void {
        if (url) {
            const head = this._document.getElementsByTagName('head')[0];
            let element: HTMLLinkElement | null = this._document.querySelector(`link[rel='canonical']`) || null;
            if (element == null) {
                element = this._document.createElement('link') as HTMLLinkElement;
                head.appendChild(element);
            }
            element.setAttribute('rel', 'canonical');
            element.setAttribute('href', url);
        }
    }

    public replaceNameOfImage(img: string) {
        img = AppGlobalsModule.sanitizeRabbiImageUrl(img) || img || '';
        img = img.toLowerCase();
        //new image system (InternalFilesFake urls): keep the REAL extension the server put on the
        //fake name (webp crops) - shareImages/<id>.<ext> rewrites back to act=pictures&f=<id>
        img = img.replace(/internalfilesfake\.([a-z]+)\?act=pictures&f=([0-9]+)\S*/i, 'ShareImages/$2.$1');
        const regex = /(InternalFiles\.ashx\?act=pictures&f=)([0-9]+)/i;
        //https://pic.yeshiva.org.il/siteprojects/yeshivamodels/logic/images/spriterszround.aspx?pic=v1-39709-brin_roush78?v=20180904120041
        img = img.replace(regex, 'ShareImages/$2.png');
        // spriteRszRound rabbi/midrash URLs are already normalized (and their v/cacheVer query
        // preserved) by sanitizeRabbiImageUrl above, so no further rewrite is needed here.
        if (img.startsWith('/')) {
            img = AppUtilsModule.createHost(environment.pic.protocol, environment.pic.domain, environment.pic.port) + img;
        }
        return img;
    }

    public setMetaImage(img: string, imgAtlt: string | null = null): void {
        img = this.replaceNameOfImage(img);
        this.setMetaTagByProperty('og:image', img, 'image');
        this.setMetaTagByProperty('og:image:secure', img, 'image');
        this.setMetaTag('twitter:image', img);
        if (imgAtlt) {
            this.setMetaTagByProperty('og:image:alt', imgAtlt);
            this.setMetaTag('twitter:image:alt', imgAtlt);
        }
    }

    public updateDisplayTitle(newDisplayTitle: string | null) {
        //TODO - make this change when location change forword
        if (!this._historyRoute.isPopBackNavigation) {
            this.pushToHistoryTitles(this.displayTitle);
        }
        this.displayTitle = newDisplayTitle ?? '';
    }

    public pushToHistoryTitles(lastTitle: string) {
        this.historyTitlesStack.push(lastTitle);
    }

    public popFromHistoryTitles(): string {
        return this.historyTitlesStack.pop() ?? '';
    }

    public getLastTitle(): string | null {
        if (this.historyTitlesStack.length > 0) {
            return this.historyTitlesStack[this.historyTitlesStack.length - 1];
        }
        return null;
    }
}
